import { Button, DropdownDivider, DropdownItem, Heading, HFlow, Icon, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { ButtonSize } from 'bold-ui/lib/components/Button/Button/ButtonSkins'
import { useAlert } from 'components/alert'
import useSession from 'components/auth/useSession'
import { DateTime } from 'components/date'
import { Age } from 'components/date/Age'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { toEsusParameters } from 'components/esus'
import { Cpf, Telefone } from 'components/label'
import { confirm } from 'components/modals/confirm'
import { useExcluirCidadaoMutation } from 'graphql/hooks.generated'
import qs from 'qs'
import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { identidadeGenero, sexo } from 'types/enums'
import Permissions from 'types/Permissions'
import { titleCase } from 'util/strConversor'

import { JustificativaModal } from '../JustificativaModal'
import { CidadaoCabecalho } from '../types/CidadaoCabecalho'

export interface CidadaoCabecalhoSectionProps {
  cidadao: CidadaoCabecalho
  minimizado?: boolean
  sizeBotaoAtualizar?: ButtonSize
  botaoVerProntuario?: boolean
  botaoVerAgendamentos?: boolean
  botaoExcluirCidadao?: boolean
  botaoSelecionar?: boolean
  callbackUrl?: string
  callbackParams?: string
}

export const CidadaoCabecalhoSection = (props: CidadaoCabecalhoSectionProps) => {
  const { minimizado } = props
  return (
    <HFlow alignItems='flex-start' hSpacing={0.5} justifyContent='space-between' style={{ marginTop: '1rem' }}>
      {minimizado ? <InformacoesCadastraisSimplificadas {...props} /> : <InformacoesCadastrais {...props} />}
      <Acoes
        cidadao={props.cidadao}
        sizeBotaoAtualizar={props.sizeBotaoAtualizar}
        botaoVerProntuario={props.botaoVerProntuario}
        botaoVerAgendamentos={props.botaoVerAgendamentos}
        botaoExcluirCidadao={props.botaoExcluirCidadao}
        botaoSelecionar={props.botaoSelecionar}
        callbackUrl={props.callbackUrl}
        callbackParams={props.callbackParams}
      />
    </HFlow>
  )
}

export const CidadaoNomeSection = (props: CidadaoCabecalhoSectionProps) => {
  const { cidadao, minimizado } = props

  return cidadao?.nomeSocial ? (
    <Heading data-testid='CidadaoCabecalho.nomeSocial' level={1} style={minimizado ? { fontSize: '1rem' } : {}}>
      {titleCase(cidadao?.nomeSocial)}
      <span style={{ fontSize: minimizado ? '0.8rem' : '1rem' }}> (Nome social) </span>
      <span style={{ fontSize: '1rem', fontWeight: 'normal' }} data-testid='CidadaoCabecalho.nome'>
        | {titleCase(cidadao?.nome)}
      </span>
      {cidadao?.faleceu && (
        <Tag style={{ marginLeft: '0.5rem' }} data-testid='CidadaoCabecalho.marcacaoObito'>
          Óbito
        </Tag>
      )}
      {!cidadao?.ativo && (
        <Tag style={{ marginLeft: '0.5rem' }} data-testid='CidadaoCabecalho.marcacaoInativo'>
          Inativo
        </Tag>
      )}
      {cidadao?.unificado && (
        <Tag style={{ marginLeft: '0.5rem' }} data-testid='CidadaoCabecalho.marcacaoCadastroUnificado'>
          Cadastro unificado
        </Tag>
      )}
    </Heading>
  ) : (
    <HFlow alignItems='baseline' hSpacing={0.5}>
      <Heading data-testid='CidadaoCabecalho.nome' level={1}>
        {titleCase(cidadao?.nome)}
      </Heading>
      {cidadao?.faleceu && <Tag data-testid='CidadaoCabecalho.marcacaoObito'>Óbito</Tag>}
      {!cidadao?.ativo && <Tag data-testid='CidadaoCabecalho.marcacaoInativo'>Inativo</Tag>}
      {cidadao?.unificado && <Tag data-testid='CidadaoCabecalho.marcacaoCadastroUnificado'>Cadastro unificado</Tag>}
    </HFlow>
  )
}

export const InformacoesCadastrais = (props: CidadaoCabecalhoSectionProps) => {
  const { cidadao } = props
  const telefone = cidadao?.telefoneCelular || cidadao?.telefoneResidencial

  return (
    <VFlow vSpacing={0}>
      <CidadaoNomeSection {...props} />

      <HFlow hSpacing={4}>
        <VFlow vSpacing={0}>
          <HFlow hSpacing={1}>
            <HFlow alignItems='center' hSpacing={0.5} data-testid='CidadaoCabecalho.cpf'>
              <Text fontWeight='bold'>CPF</Text>
              {cidadao?.cpf ? <Cpf value={cidadao?.cpf} /> : <Text>-</Text>}
            </HFlow>
            <HFlow hSpacing={0.5} data-testid='CidadaoCabecalho.cns'>
              <Text fontWeight='bold'>CNS</Text>
              {cidadao?.cns ? <Text>{cidadao?.cns}</Text> : <Text>-</Text>}
            </HFlow>
          </HFlow>
          <HFlow alignItems='center' hSpacing={0.5} data-testid='CidadaoCabecalho.dataNascimento'>
            <Text fontWeight='bold'>Data de nascimento</Text>
            <DateTime format='DD/MM/YYYY' value={cidadao?.dataNascimento} />
            <Age dateOfBirth={cidadao?.dataNascimento} />
          </HFlow>
          {cidadao?.identidadeGeneroDbEnum ? (
            <HFlow hSpacing={1}>
              <HFlow hSpacing={0.5}>
                <Text fontWeight='bold'>Sexo</Text>
                <Text data-testid='CidadaoCabecalho.sexo'>{sexo[cidadao?.sexo]}</Text>
              </HFlow>
              <HFlow hSpacing={0.5}>
                <Text fontWeight='bold'>Id. de gênero</Text>
                <Text data-testid='CidadaoCabecalho.identidadeGenero'>
                  {identidadeGenero[cidadao?.identidadeGeneroDbEnum]}
                </Text>
              </HFlow>
            </HFlow>
          ) : (
            <HFlow hSpacing={0.5}>
              <Text fontWeight='bold'>Sexo</Text>
              <Text data-testid='CidadaoCabecalho.sexo'>{sexo[cidadao?.sexo]}</Text>
            </HFlow>
          )}
        </VFlow>

        <VFlow vSpacing={0}>
          <HFlow hSpacing={0.5} data-testid='CidadaoCabecalho.nomeMae'>
            <Text fontWeight='bold'>Nome da mãe</Text>
            {cidadao?.nomeMae ? <Text>{titleCase(cidadao?.nomeMae)}</Text> : <Text>-</Text>}
          </HFlow>
          <HFlow alignItems='center' hSpacing={0.5} data-testid='CidadaoCabecalho.telefone'>
            <Text fontWeight='bold'>Telefone</Text>
            {telefone ? <Telefone value={telefone}></Telefone> : <Text>-</Text>}
          </HFlow>
          <HFlow hSpacing={0.5} data-testid='CidadaoCabecalho.unidadeResponsavel'>
            <Text fontWeight='bold'>Unidade responsável</Text>
            <Text>{titleCase(cidadao?.cidadaoVinculacaoEquipe?.unidadeSaude?.nome) ?? '-'}</Text>
          </HFlow>
        </VFlow>
      </HFlow>
    </VFlow>
  )
}

export const InformacoesCadastraisSimplificadas = (props: CidadaoCabecalhoSectionProps) => {
  const { cidadao } = props

  return (
    <VFlow vSpacing={0}>
      <CidadaoNomeSection {...props} />

      <HFlow hSpacing={4}>
        <VFlow vSpacing={0}>
          <HFlow hSpacing={1}>
            <HFlow alignItems='center' hSpacing={0.5} data-testid='CidadaoCabecalho.cpf'>
              <Text fontWeight='bold'>CPF</Text>
              {cidadao?.cpf ? <Cpf value={cidadao?.cpf} /> : <Text>-</Text>}
            </HFlow>
            <HFlow hSpacing={0.5} data-testid='CidadaoCabecalho.cns'>
              <Text fontWeight='bold'>CNS</Text>
              {cidadao?.cns ? <Text>{cidadao?.cns}</Text> : <Text>-</Text>}
            </HFlow>
            <HFlow alignItems='center' hSpacing={0.5} data-testid='CidadaoCabecalho.dataNascimento'>
              <Text fontWeight='bold'>Data de nascimento</Text>
              <DateTime format='DD/MM/YYYY' value={cidadao?.dataNascimento} />
              <Age dateOfBirth={cidadao?.dataNascimento} />
            </HFlow>
          </HFlow>
        </VFlow>
      </HFlow>
    </VFlow>
  )
}

export const Acoes = (props: CidadaoCabecalhoSectionProps) => {
  const {
    cidadao,
    sizeBotaoAtualizar,
    botaoVerProntuario,
    botaoVerAgendamentos,
    botaoExcluirCidadao,
    botaoSelecionar,
    callbackUrl,
    callbackParams,
  } = props
  const { hasAuthorization } = useSession()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const cadastrarEEditar = hasAuthorization(Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao.cadastrarEEditar)
  const excluir = hasAuthorization(Permissions.gestaoDeCadastrosDeCidadao.visualizarCidadao.excluir)
  const visualizarProntuario = hasAuthorization(Permissions.visualizarProntuario)
  const visualizarAgenda = hasAuthorization(Permissions.visualizarAgenda)

  const allPermissions = excluir || visualizarProntuario || visualizarAgenda
  const showDropdownMenu = botaoVerProntuario || botaoVerAgendamentos || botaoExcluirCidadao

  const [excluirCidadao] = useExcluirCidadaoMutation()
  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const history = useHistory()
  const location = useLocation()

  const handleExcluir = () => {
    confirm({
      title: `Deseja excluir o cadastro do cidadão?`,
      confirmLabel: 'Excluir',
      type: 'danger',
      onConfirm: () => {
        return excluirCidadao({ variables: { cidadaoId: cidadao?.id } })
          .then(() => {
            alert('success', 'Cadastro do cidadão excluído')
            history.push(`/cidadao`)
          })
          .catch(handleRejection)
      },
    })()
  }

  const onVisualizarProntuarioClick = () => {
    setIsModalOpen(true)
  }

  const handleVerAgendamentosClick = () => {
    history.push(
      `/cidadao/visualizarAgendamento?${toEsusParameters({
        cidadaoId: cidadao?.id,
        cidadaoNome: cidadao?.nome,
        prontuarioId: cidadao?.prontuario?.id,
        pecBackLink: location.pathname + location.search,
        showCidadaoLink: 0,
      })}`
    )
  }

  const onEditarClick = () => {
    if (!!callbackUrl) {
      history.push(`/cidadao/${props.cidadao.id}/edit?callbackUrl=${callbackUrl}&callbackParams=${callbackParams}`)
    } else {
      history.push(`/cidadao/${props.cidadao.id}/edit${location.search}`)
    }
  }

  const navigateToExternalUrl = () => {
    if (callbackUrl === 'lista-atendimento' || callbackUrl === 'ad/lista/cadastro') {
      history.push(`/${callbackUrl}?cidadaoId=${props.cidadao.id}`)
    } else {
      //callback para módulos antigos - remover toda a condição quando os módulos serem refeitos
      const url = atob(callbackUrl)
      const decodedParams = atob(callbackParams)
      const path = url === '/pec/user/registrotardiolisting/registrotardiodetail' ? 'registroTardio' : 'agenda'
      history.push(
        `/${path}?iframeUrl=${url}?${btoa(
          decodedParams + '&' + qs.stringify({ cidadaoCadastradoId: props.cidadao.id })
        )}`
      )
    }
  }

  return (
    <>
      {visualizarProntuario && botaoVerProntuario && (
        <JustificativaModal
          cidadaoId={cidadao.id}
          isModalOpen={isModalOpen}
          handleModalClose={() => setIsModalOpen(false)}
        />
      )}
      <VFlow vSpacing={0.3}>
        <HFlow hSpacing={0.5} justifyContent='flex-end'>
          {cadastrarEEditar && (
            <Tooltip text={!cidadao?.ativo && 'Este cadastro não pode ser atualizado pois está inativo'}>
              <Button
                disabled={!cidadao?.ativo}
                kind='primary'
                onClick={onEditarClick}
                size={sizeBotaoAtualizar}
                skin='default'
                data-testid='CidadaoCabecalho.atualizarCadastro'
              >
                Atualizar cadastro
              </Button>
            </Tooltip>
          )}
          {allPermissions && showDropdownMenu && (
            <DropdownButton
              kind='normal'
              size={sizeBotaoAtualizar}
              skin='outline'
              data-testid='CidadaoCabecalho.dropDown'
            >
              {visualizarProntuario && botaoVerProntuario && (
                <Tooltip text={!cidadao?.prontuario && 'Cadastro não possui prontuário'}>
                  <DropdownItem
                    data-testid='CidadaoCabecalho.botaoVerProntuario'
                    onClick={onVisualizarProntuarioClick}
                    disabled={!cidadao?.prontuario}
                  >
                    Ver prontuário
                  </DropdownItem>
                </Tooltip>
              )}

              {visualizarAgenda && botaoVerAgendamentos && (
                <Tooltip text={!cidadao?.possuiAgendamento && 'Não há agendamentos registrados para o cidadão'}>
                  <DropdownItem
                    data-testid='CidadaoCabecalho.botaoVerAgendamentos'
                    onClick={handleVerAgendamentosClick}
                    disabled={!cidadao?.possuiAgendamento}
                  >
                    Ver agendamentos
                  </DropdownItem>
                </Tooltip>
              )}
              {botaoExcluirCidadao && excluir && (
                <>
                  <DropdownDivider />
                  <DropdownItem
                    type='danger'
                    style={{
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={handleExcluir}
                    data-testid='CidadaoCabecalho.botaoExcluir'
                  >
                    <Icon icon='trashOutline' style={{ marginRight: '0.5rem' }} /> Excluir
                  </DropdownItem>
                </>
              )}
            </DropdownButton>
          )}
        </HFlow>
        <HFlow hSpacing={0.2} alignItems='center' data-testid='CidadaoCabecalho.dataUltimaAtualizacao'>
          <Text>Última atualização em</Text>
          <DateTime format='DD/MM/YYYY' value={cidadao?.dataAtualizado} />
        </HFlow>
        {botaoSelecionar && (
          <HFlow hSpacing={0.2} justifyContent='flex-end'>
            <Button kind='normal' skin='outline' size='small' onClick={navigateToExternalUrl}>
              <Icon icon='plus' style={{ marginRight: '0.5rem' }} />
              Selecionar e voltar
            </Button>
          </HFlow>
        )}
      </VFlow>
    </>
  )
}
